import {type MouseEvent, useState} from "react";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import {
    Box,
    Button,
    Divider,
    IconButton,
    Menu,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {alpha, styled} from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import TranslateIcon from "@mui/icons-material/Translate";
import {useTranslation} from "react-i18next";
import {useChangeLanguage} from "../../api/settings/settingsQueries";
import ConfirmDialog from "../dialog/ConfirmDialog";
import {useLogoutMutation} from "../../api/login/logoutQueries";
import {setLogout} from "../../util/loginStatus";
import {useNavigate} from "react-router-dom";
import {useThemeMode} from "../../theme/AppThemeProvider";
import ArenaSnackBar from "../ArenaSnackBar";
import ChangePasswordDialog from "./ChangePasswordDialog";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({theme}) => ({
    backgroundColor:
        theme.palette.mode === "light"
            ? alpha(theme.palette.primary.main, 0.06)
            : alpha(theme.palette.primary.light, 0.08),
    padding: theme.spacing(0.5),
    borderRadius: 8,
    border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "light" ? 0.12 : 0.18)}`,
    "& .MuiToggleButton-root": {
        textTransform: "none",
        fontWeight: 600,
        border: "1px solid transparent",
        borderRadius: 6,
        color: theme.palette.text.primary,
        gap: theme.spacing(0.75),
        opacity: 0.88,
        transition: "background-color 140ms ease, border-color 140ms ease, color 140ms ease, opacity 140ms ease",
        "&.Mui-selected": {
            backgroundColor:
                theme.palette.mode === "light"
                    ? theme.palette.background.paper
                    : alpha(theme.palette.primary.main, 0.16),
            color: theme.palette.primary.main,
            borderColor: alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.2 : 0.36),
            boxShadow:
                theme.palette.mode === "light"
                    ? "0 2px 6px rgba(0,0,0,0.1)"
                    : `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.12)}`,
            opacity: 1,
            "&:hover": {
                backgroundColor:
                    theme.palette.mode === "light"
                        ? theme.palette.background.paper
                        : alpha(theme.palette.primary.main, 0.2),
            },
        },
        "&:hover": {
            backgroundColor:
                theme.palette.mode === "light"
                    ? alpha(theme.palette.background.paper, 0.78)
                    : alpha(theme.palette.background.paper, 0.28),
            borderColor: alpha(theme.palette.text.primary, theme.palette.mode === "light" ? 0.12 : 0.22),
            color: theme.palette.text.primary,
            opacity: 1,
        },
    },
}));

const SectionLabel = styled(Typography)(({theme}) => ({
    display: "block",
    marginBottom: theme.spacing(0.35),
    color: theme.palette.text.secondary,
    fontWeight: 700,
    letterSpacing: 1,
}));

const SettingsMenu: React.FC = () => {
    const {t: translate, i18n} = useTranslation();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
    const isMenuOpen = Boolean(menuAnchorEl);
    const language = i18n.language.toLowerCase().startsWith("de") ? "de" : "en";
    const changeLanguageMutation = useChangeLanguage();
    const {themeMode, setThemeMode} = useThemeMode();
    const logoutMutation = useLogoutMutation();
    const navigate = useNavigate();

    const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleLanguageChange = async (_: MouseEvent<HTMLElement>, value: string | null) => {
        if (!value) {
            return;
        }

        try {
            await changeLanguageMutation.mutateAsync({preferredLocale: value});
        } catch {
            return;
        }
    };

    const handleLogoutClick = () => {
        handleMenuClose();
        setIsLogoutDialogOpen(true);
    };

    const handleChangePasswordClick = () => {
        handleMenuClose();
        setIsChangePasswordDialogOpen(true);
    };

    const handleChangePasswordDialogClose = () => {
        setIsChangePasswordDialogOpen(false);
    };

    const handleLogoutDialogClose = () => {
        if (logoutMutation.isPending) {
            return;
        }
        setIsLogoutDialogOpen(false);
    };

    const handleLogoutConfirm = async () => {
        try {
            await logoutMutation.mutateAsync();
        } catch (error) {
            console.error(error);
        } finally {
            setLogout();
            setIsLogoutDialogOpen(false);
            navigate("/login", {replace: true});
        }
    };

    const handleThemeChange = (_: MouseEvent<HTMLElement>, value: string | null) => {
        if (value !== "light" && value !== "dark") {
            return;
        }

        setThemeMode(value);
    };

    const handleLanguageChangeErrorClose = () => {
        changeLanguageMutation.reset();
    };

    return (
        <Box sx={{display: "flex", alignItems: "center"}}>
            <IconButton
                onClick={handleMenuOpen}
                aria-label={translate("settings.openMenu")}
                sx={{
                    width: 40,
                    height: 40,
                    border: "1px solid",
                    borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.24 : 0.36),
                    backgroundColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.1 : 0.18),
                    color: "inherit",
                    boxShadow: (theme) =>
                        `0 2px 10px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.22 : 0.14)}`,
                    backdropFilter: "blur(10px)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        backgroundColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.18 : 0.28),
                        borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.32 : 0.48),
                        transform: "translateY(-1px)",
                        boxShadow: (theme) =>
                            `0 6px 16px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.28 : 0.18)}`,
                    },
                }}
            >
                <SettingsIcon sx={{fontSize: 20, color: "inherit"}}/>
            </IconButton>

            <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1.5,
                            borderRadius: 3,
                            minWidth: 240,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                            overflow: "visible",
                            // Adding a small arrow at the top
                            "&:before": {
                                content: '""',
                                display: "block",
                                position: "absolute",
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: "background.paper",
                                transform: "translateY(-50%) rotate(45deg)",
                                zIndex: 0,
                                borderLeft: "1px solid",
                                borderTop: "1px solid",
                                borderColor: "divider",
                            },
                        },
                    },
                }}
            >
                <Box sx={{p: 1.5, pt: 0.5}}>
                    {/* Header Area */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.75}}>
                        <Box
                            sx={{
                                p: 0.6,
                                borderRadius: 1.5,
                                bgcolor: "primary.lighter", // Or use 'rgba(25, 118, 210, 0.08)'
                                display: "flex",
                            }}
                        >
                            <TranslateIcon fontSize="small" color="primary" />
                        </Box>
                        <Typography variant="subtitle1" sx={{fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.1}}>
                            {translate("settings.title")}
                        </Typography>
                    </Box>
                    <Divider sx={{mb: 0.75}} />

                    <SectionLabel variant="overline">{translate("settings.language")}</SectionLabel>

                    <StyledToggleButtonGroup
                        value={language}
                        exclusive
                        onChange={handleLanguageChange}
                        size="small"
                        fullWidth
                    >
                        <ToggleButton value="en">{translate("settings.en")}</ToggleButton>
                        <ToggleButton value="de">{translate("settings.de")}</ToggleButton>
                    </StyledToggleButtonGroup>

                    <Divider sx={{my: 1.25}} />

                    <SectionLabel variant="overline">{translate("settings.theme")}</SectionLabel>

                    <StyledToggleButtonGroup
                        value={themeMode}
                        exclusive
                        onChange={handleThemeChange}
                        size="small"
                        fullWidth
                    >
                        <ToggleButton value="light">
                            <LightModeOutlinedIcon sx={{fontSize: 18}} />
                            {translate("settings.light")}
                        </ToggleButton>
                        <ToggleButton value="dark">
                            <DarkModeOutlinedIcon sx={{fontSize: 18}} />
                            {translate("settings.dark")}
                        </ToggleButton>
                    </StyledToggleButtonGroup>

                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleChangePasswordClick}
                        sx={{
                            mt: 1.25,
                            justifyContent: "flex-start",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                        }}
                    >
                        {translate("settings.changePassword")}
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogoutClick}
                        sx={{
                            mt: 1.25,
                            color: "error.main",
                            borderColor: "error.main",
                            justifyContent: "flex-start",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            "& .MuiButton-startIcon": {
                                color: "error.main",
                            },
                            "&:hover": {
                                borderColor: "error.dark",
                                backgroundColor: "error.lighter",
                            },
                        }}
                    >
                        {translate("settings.logout")}
                    </Button>
                </Box>
            </Menu>
            <ConfirmDialog
                open={isLogoutDialogOpen}
                message={translate("settings.logoutConfirmation")}
                confirmLabel={translate("common.yes")}
                cancelLabel={translate("common.no")}
                onConfirm={() => void handleLogoutConfirm()}
                onClose={handleLogoutDialogClose}
                isLoading={logoutMutation.isPending}
            />
            <ChangePasswordDialog
                open={isChangePasswordDialogOpen}
                onClose={handleChangePasswordDialogClose}
            />
            <ArenaSnackBar message={changeLanguageMutation.error?.message} onClose={handleLanguageChangeErrorClose} />
        </Box>
    );
};

export default SettingsMenu;
